import { config } from "@config/config";
import { datadogRum } from "@datadog/browser-rum";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { SUPPORT_FORM_URL } from "@utils/constants";
import { isNewSyncStamp, manualSyncStartFailure, orgEhrUnusableMessage } from "@utils/ehrActions";
import { isSyncDone, learningTreeReviewVariables, useLearningTreeReviewEnabled } from "@utils/learningTreeReview";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { Button, Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  ApiLearnerManualGoalSyncStatusChoices,
  ReportStatusExtendedEnums,
  useLearnerManualGoalSyncByIdQuery,
  useStartManualSyncWorkflowMutation,
} from "@api/graphql/types-and-hooks";
import { EHRLoginDialog } from "@components/ClinicalNewReportDialog/EHRLoginDialog";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { useAdminData } from "@providers/AdminDataProvider";
import { useBackgroundSync } from "@providers/BackgroundSyncProvider";
import { useEHRConnection } from "@providers/EHRConnectionProvider";
import { useReport } from "@providers/ReportProvider";

interface EHRProgrammingRefreshProps {
  learnerExternalId: string | null;
  onImportSuccess: () => void;
  /**
   * CR learning tree review: an import is already running (Lock started it),
   * so wait for it instead of starting one. The value is the client's sync
   * stamp from before it started (null: none yet); only a status written after
   * it settles the page. Retry still starts a refresh as usual.
   */
  watchImportSince?: string | null;
  /** CR learning tree review: read only the learning tree (Refresh on the tree screen). */
  learningTreeStructureOnly?: boolean;
}

export const EHRProgrammingRefresh: React.FC<EHRProgrammingRefreshProps> = ({
  learnerExternalId,
  onImportSuccess,
  watchImportSince,
  learningTreeStructureOnly = false,
}) => {
  const watchingImport = watchImportSince !== undefined;
  const queryClient = useQueryClient();
  const { report } = useReport();
  const { startBackgroundSync } = useBackgroundSync();
  const {
    isOrgUsernamePassword,
    ehrConnectionStatus,
    orgEhrUnusable,
    noCredentials,
    hasCredentials,
    isCredentialsLoading,
    retryConnection,
  } = useEHRConnection();
  const { isMotivityUser } = useAdminData();
  const [notified, setNotified] = useState(false);
  // True while a failed-import retry is reconnecting the EHR (client-list refresh)
  // before the report refresh. Authoritatively drives the loading state and
  // suppresses any stale Temporal-failure error for the duration, independent of
  // the awaitingFreshSync/freshness machinery (PROA-1533).
  const [isReconnecting, setIsReconnecting] = useState(false);
  const awaitingFreshSync = useRef(true);
  const awaitingFreshSyncTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Freshness is judged by comparing server timestamps to each other, not
  // to a client clock: manualGoalSyncLastUpdated is server-written, and a
  // user whose clock runs ahead of the server by more than the sync takes
  // would otherwise see a genuinely fresh COMPLETED judged stale. We keep
  // the value seen on the first poll and treat any *different* value as
  // this visit's result.
  const baselineSyncStampRef = useRef<string | null | undefined>(watchImportSince);
  // Mirrors importLastUpdated for clientImportFromEHR, which must not take
  // it as a dependency: the callback identity feeds the auto-start effect.
  const importLastUpdatedRef = useRef<string | null | undefined>(undefined);
  // Watching an import that's already running counts as started: no auto-start.
  const hasAutoStartedRef = useRef(watchingImport);

  const clientFirstName = report?.reportClientDetails?.clientFirstName || "";
  const learnerId = report?.learner?.id || "";
  const reportId = report?.id || "";
  const learningTreeReview = useLearningTreeReviewEnabled();
  const clientName = clientFirstName.length ? `${clientFirstName}\u2019s` : "";

  // PROA-1954: the credential-failure signal for a reopened report lives on the
  // already-loaded report (extendedStatus IMPORT_FAILED + a persisted FAILED_CREDS
  // importAlert), independent of any live polling result. When the previous import
  // failed on invalid credentials we must not auto-retry with the same stored
  // credentials — that just loops through failures — so prompt the user to
  // re-enter them first. Requiring IMPORT_FAILED (not just the alert) avoids acting
  // on a stale credential alert from an earlier run that later succeeded.
  const prevImportFailedCredentials =
    report?.extendedStatus === ReportStatusExtendedEnums.ImportFailed &&
    !!report?.ehrSyncAlerts?.importAlert?.error?.includes("FAILED_CREDS");
  // Cleared once a retry actually starts: clientImportFromEHR/reconnectThenRefresh
  // flip hasAutoStartedRef, so a successful credential re-entry hands off to the
  // normal loading state on the next render. Reading the ref during render is safe
  // because the retry path triggers a state change (mutate → isImportPending).
  const credentialsBlocked = prevImportFailedCredentials && !hasAutoStartedRef.current;

  const startAwaitingTimeout = useCallback(() => {
    if (awaitingFreshSyncTimeout.current) clearTimeout(awaitingFreshSyncTimeout.current);
    awaitingFreshSyncTimeout.current = setTimeout(() => {
      awaitingFreshSync.current = false;
    }, 30_000);
  }, []);

  const {
    data: startImportData,
    isError: isImportError,
    isIdle: isImportIdle,
    isPending: isImportPending,
    mutate,
  } = useStartManualSyncWorkflowMutation({
    onSuccess: (data) => {
      if (manualSyncStartFailure(data.startManualSyncWorkflow)) {
        datadogRum.addAction("Reassessment import did not start", { learnerExternalId });
        // Most refusals are about the EHR connection. Refreshing it lets
        // noCredentials catch up, so Retry opens the login dialog when needed.
        void queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
        void queryClient.invalidateQueries({ queryKey: ["getHasEhrCredentials"] });
        return;
      }
      datadogRum.addAction("Reassessment import started successfully", { learnerExternalId });
      startBackgroundSync({ learnerId, reportId, clientFirstName });
      startAwaitingTimeout();
    },
    onError: (error) => {
      datadogRum.addError(error, { context: "Reassessment import failed", learnerExternalId });
    },
  });
  // FDM refused to start the import (status false), so no sync is running and
  // the page shows its message instead of waiting on the learner status. A new
  // attempt clears it, because the mutation drops its data when it runs again.
  const startFailureMessage = startImportData ? manualSyncStartFailure(startImportData.startManualSyncWorkflow) : null;

  const { data: importStatusData, refetch: refetchImportStatus } = useLearnerManualGoalSyncByIdQuery(
    { learnerId },
    {
      gcTime: 0,
      staleTime: 0,
      refetchInterval: (query) => {
        const learner = query?.state?.data?.learnerById;
        const status = learner?.manualGoalSyncStatus;
        const lastUpdated = learner?.manualGoalSyncLastUpdated;
        // Mirrors isSyncSettled below, freshness included. Stopping on a
        // terminal status left over from an earlier run would strand the
        // page: isSyncSettled rejects it as stale, so the spinner stays up
        // and there are no further polls to observe the real result.
        const fresh = isNewSyncStamp(lastUpdated, baselineSyncStampRef.current);
        if (
          fresh &&
          (isSyncDone(status) ||
            (status === ApiLearnerManualGoalSyncStatusChoices.Failure && !awaitingFreshSync.current))
        ) {
          return false;
        }
        return config.REPORT_POLLING_INTERVAL;
      },
      // Not gated on isImportSuccess. The learner status is the durable
      // record of the import; the mutation is only the trigger. Waiting for
      // the mutation's promise to settle meant a slow or lost response pinned
      // this page on its spinner forever even though the sync had finished
      // (INT-36). Freshness is enforced by isSyncFresh below instead.
      // Not on the screens where no sync is ever started. The auto-start
      // effect is blocked on all three, so nothing would ever advance the
      // learner status, refetchInterval would never return false, and the
      // page would poll FDM for as long as an error screen stayed mounted
      // — with gcTime/staleTime 0, i.e. a real request every time.
      enabled: !!learnerId && !credentialsBlocked && !orgEhrUnusable && !noCredentials && !startFailureMessage,
    },
  );

  const importStatus = importStatusData?.learnerById?.manualGoalSyncStatus;
  const importLastUpdated = importStatusData?.learnerById?.manualGoalSyncLastUpdated;

  // Some refusals also save a fresh Failure to the learner. The poll is off
  // on this screen, so read it once (refetch ignores `enabled`): Retry
  // baselines on that stamp and must not take the refusal's Failure for its own.
  useEffect(() => {
    if (startFailureMessage) void refetchImportStatus();
  }, [startFailureMessage, refetchImportStatus]);

  useEffect(() => {
    if (!importStatus) return;
    // A Failure written since this attempt started (a stamp other than the
    // baseline) is definitive at once, so an immediate failure like FAILED_CREDS
    // shows without waiting for the 30 s awaitingFreshSync timer, whatever the
    // browser clock says. A Failure still on the baseline stamp was there before
    // this attempt: on a quick Retry it is the previous error.
    const isFreshFailure =
      importStatus === ApiLearnerManualGoalSyncStatusChoices.Failure &&
      isNewSyncStamp(importLastUpdated, baselineSyncStampRef.current);
    if (importStatus !== ApiLearnerManualGoalSyncStatusChoices.Failure || isFreshFailure) {
      awaitingFreshSync.current = false;
      if (awaitingFreshSyncTimeout.current) {
        clearTimeout(awaitingFreshSyncTimeout.current);
        awaitingFreshSyncTimeout.current = null;
      }
    }
  }, [importStatus, importLastUpdated]);

  useEffect(() => {
    return () => {
      if (awaitingFreshSyncTimeout.current) clearTimeout(awaitingFreshSyncTimeout.current);
    };
  }, []);

  // Watching an import started elsewhere: what a successful start does here,
  // once (the background sync toast if the user leaves, and the wait for a
  // fresh result).
  const watchStartedRef = useRef(false);
  useEffect(() => {
    if (!watchingImport || watchStartedRef.current || !learnerId || !reportId) return;
    watchStartedRef.current = true;
    startBackgroundSync({ learnerId, reportId, clientFirstName });
    startAwaitingTimeout();
  }, [watchingImport, learnerId, reportId, clientFirstName, startBackgroundSync, startAwaitingTimeout]);

  // Only a status the server wrote *after* this page started the sync can
  // settle it. Without this the query — no longer gated on the mutation —
  // could serve a COMPLETED from an earlier run and complete the page before
  // the fresh import had even been queued.
  // Stays `undefined` until the query has actually answered. Collapsing an
  // unanswered query to `null` here would let an auto-start (which fires
  // before the first poll returns) re-baseline to `null`, so the first
  // response — a leftover stamp from an earlier run — would read as fresh.
  importLastUpdatedRef.current = importStatusData === undefined ? undefined : importLastUpdated ?? null;
  if (baselineSyncStampRef.current === undefined && importStatusData !== undefined) {
    baselineSyncStampRef.current = importLastUpdated ?? null;
  }
  // Different from what the learner carried when this page opened ⇒ the
  // server wrote it for this visit.
  const isSyncFresh = isNewSyncStamp(importLastUpdated, baselineSyncStampRef.current);
  // The effect above clears awaitingFreshSync only after this render, and a
  // poll that returns the same data renders nothing more, so a fresh Failure
  // is read here, in the render that receives it.
  const isAwaitingFreshSync =
    awaitingFreshSync.current && !(isSyncFresh && importStatus === ApiLearnerManualGoalSyncStatusChoices.Failure);
  // A fresh terminal status outranks the in-flight mutation: whatever it
  // started (or joined) has already finished, so isImportPending must not
  // keep the spinner up.
  const isSyncSettled =
    isSyncFresh &&
    (isSyncDone(importStatus) ||
      (importStatus === ApiLearnerManualGoalSyncStatusChoices.Failure && !isAwaitingFreshSync));

  // A COMPLETED the page opened on belongs to an earlier run. isSyncSettled
  // already refuses to settle on it, but without this the status also matches
  // none of the in-progress cases below, so the page would fall through to
  // isCompleted and let the reassessment proceed on the previous visit's
  // data — on mount, before this visit's sync has even been queued.
  const isStaleCompleted = !isSyncFresh && isSyncDone(importStatus);
  const isImportInProgress =
    !isSyncSettled &&
    (isImportPending ||
      !importStatus ||
      isStaleCompleted ||
      (importStatus === ApiLearnerManualGoalSyncStatusChoices.Failure && isAwaitingFreshSync) ||
      importStatus === ApiLearnerManualGoalSyncStatusChoices.Inprogress ||
      importStatus === ApiLearnerManualGoalSyncStatusChoices.Pending);
  // credentialsBlocked short-circuits loading: no import ever started, so
  // importStatus is undefined and isImportInProgress would otherwise spin forever.
  const isLoading = !credentialsBlocked && (isImportInProgress || isReconnecting);
  // Only treat sync as failed-from-Temporal when the workflow itself reports
  // FAILURE (the activity ran and persisted an alert). A mutation-layer
  // `isImportError` means the workflow never started, so no fresh alert was
  // written for this attempt. A refused start did not run it either, even when
  // FDM saved a Failure while refusing it.
  const isTemporalFailure =
    !startFailureMessage && importStatus === ApiLearnerManualGoalSyncStatusChoices.Failure && !isAwaitingFreshSync;
  const [ehrLoginDialogOpen, setEhrLoginDialogOpen] = useState(false);
  // Set once a relayed MFA code has started this login's import. The dialog
  // keeps polling until it closes, so it can still report success afterwards,
  // and that must not start a second import. Cleared when the dialog reopens.
  const [mfaImportStarted, setMfaImportStarted] = useState(false);
  const isError = isImportError || isTemporalFailure || !!startFailureMessage;
  // Only surface noCredentials as an error when the mutation hasn't started —
  // once the import is in flight (after login) show the loading state instead.
  const showError =
    !isReconnecting && (credentialsBlocked || isError || orgEhrUnusable || (noCredentials && isImportIdle));
  const isCompleted = !isLoading && !showError;

  // Refresh the report when the Temporal activity reports failure so
  // `ehrSyncAlerts` reflects the alert it just persisted (e.g. the
  // access-denied userMessage). Gated on `isTemporalFailure` rather than the
  // broader `isError` because a mutation-layer failure (`isImportError`) never
  // ran the workflow — there is no fresh alert to fetch and refetching would
  // either no-op or surface a stale alert from a prior sync.
  //
  // The key must be `["get-report-by-id"]` (hyphenated) — that's the override
  // `ClinicalReportDetailsProvider` registers its `useGetReportByIdQuery`
  // under, and the convention used throughout the rest of the codebase. The
  // codegen camelCase default matches no active query.
  useEffect(() => {
    if (isTemporalFailure) {
      queryClient.invalidateQueries({ queryKey: ["get-report-by-id"] });
    }
  }, [isTemporalFailure, queryClient]);

  // Only surface the alert userMessage when the Temporal workflow itself
  // reported FAILURE — a mutation-layer failure has no associated alert, and
  // showing whatever happens to be cached from a prior sync would mislead
  // the user.
  const importAlertUserMessage = isTemporalFailure ? report?.ehrSyncAlerts?.importAlert?.userMessage ?? null : null;
  const importAlertError = isTemporalFailure ? report?.ehrSyncAlerts?.importAlert?.error ?? null : null;
  const importAlertWarnings = isTemporalFailure ? report?.ehrSyncAlerts?.importAlert?.warnings ?? null : null;

  const clientImportFromEHR = useCallback(() => {
    if (!learnerId || !reportId) return;
    hasAutoStartedRef.current = true;
    awaitingFreshSync.current = true;
    // Re-baseline: the stamp written by the *previous* attempt is the one
    // this page is now looking at, so leaving the mount-time baseline in
    // place would make that old result read as this attempt's. On Retry
    // after a failure that meant showing the same error straight back,
    // before the new sync had written anything.
    baselineSyncStampRef.current = importLastUpdatedRef.current;
    queryClient.removeQueries({ queryKey: ["learnerManualGoalSyncByID", { learnerId }] });
    // Pass reportId so the backend can scope the read to the report's payor
    // (INT-80: a Tricare Reassessment read scopes to the Tricare CR container;
    // without a reportId the gate can't detect the payor and falls back to a
    // whole-tree read). The guard above guarantees reportId is set here.
    mutate({
      learnerId,
      reportId,
      reassessmentReportGoalSync: true,
      ...learningTreeReviewVariables(learningTreeReview),
      ...(learningTreeStructureOnly ? { learningTreeStructureOnly: true } : {}),
    });
  }, [learnerId, reportId, mutate, queryClient, learningTreeReview, learningTreeStructureOnly]);

  // Failed-import retry for a connected Motivity user: refresh the client list
  // before the report, mirroring what the disconnected path gets for free by
  // forcing a fresh EHR login. retryConnection() reconnects with stored
  // credentials AND fires importClientList (the `import_clients` scraper signal).
  // Awaiting it guarantees that signal is enqueued before startManualSyncWorkflow's
  // `get_treatment_plan`, so the scraper workflow — which dispatches queued
  // actions FIFO — refreshes the roster before the report scrape. Without this
  // the retry fired only the report refresh, so it ran against a stale roster and
  // could stall on an unresolvable client (PROA-1533). retryConnection surfaces
  // its own error toast and resolves; the defensive catch guarantees the report
  // refresh still fires even if it rejects, so this is never worse than before.
  const reconnectThenRefresh = useCallback(async () => {
    if (!learnerId || !reportId) return;
    hasAutoStartedRef.current = true;
    // Enter the reconnecting state up front so the loader shows and any stale
    // Temporal-failure error stays suppressed for the whole reconnect. The prior
    // manualGoalSync Failure is still cached until clientImportFromEHR runs, so
    // gating on this flag (rather than awaitingFreshSync) keeps the freshness
    // effect from resurfacing that error mid-reconnect.
    setIsReconnecting(true);
    await retryConnection().catch(() => undefined);
    // clientImportFromEHR removes the stale sync status and starts the report
    // refresh; clearing the flag in the same tick hands off to its own loading
    // state without a flash.
    clientImportFromEHR();
    setIsReconnecting(false);
  }, [learnerId, reportId, retryConnection, clientImportFromEHR]);

  // Scope the client-list refresh to the failed-import retry only: the happy-path
  // first refresh (ImportingClientData) keeps the direct report refresh so we
  // don't add a client-roster scrape to every reassessment. Gated on Motivity +
  // stored credentials — the disconnected/no-credentials path uses the login
  // dialog instead.
  const isImportFailedReport = report?.extendedStatus === ReportStatusExtendedEnums.ImportFailed;
  const shouldReconnectFirst = isMotivityUser && hasCredentials && isImportFailedReport;

  useEffect(() => {
    // Wait for the credentials query to resolve before deciding: hasCredentials
    // (and thus shouldReconnectFirst) is false while it loads, so auto-starting
    // now would fire the direct refresh and set hasAutoStartedRef, blocking the
    // reconnect path once credentials load (PROA-1533).
    if (
      isImportIdle &&
      !hasAutoStartedRef.current &&
      !prevImportFailedCredentials &&
      !orgEhrUnusable &&
      !noCredentials &&
      !ehrLoginDialogOpen &&
      !isCredentialsLoading
    ) {
      if (shouldReconnectFirst) {
        void reconnectThenRefresh();
      } else {
        clientImportFromEHR();
      }
    }
  }, [
    isImportIdle,
    clientImportFromEHR,
    reconnectThenRefresh,
    shouldReconnectFirst,
    prevImportFailedCredentials,
    orgEhrUnusable,
    noCredentials,
    ehrLoginDialogOpen,
    isCredentialsLoading,
  ]);

  useEffect(() => {
    if (isCompleted && !notified) {
      setNotified(true);
      onImportSuccess();
    }
  }, [isCompleted, notified, onImportSuccess]);

  // Org-level admin account: this re-assessment import polls its own status, not
  // ehr_connection, so refresh the connection when it fails so a credential
  // failure surfaces on the top badge without a manual refresh.
  useEffect(() => {
    if (isError && isOrgUsernamePassword) {
      queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
    }
  }, [isError, isOrgUsernamePassword, queryClient]);

  const renderErrorDetail = () => {
    if (orgEhrUnusable) {
      // Org-admin EHR connection isn't usable — a retry would log in with
      // the same failed org credentials, so route the user to their org
      // administrator instead of offering a doomed retry. Distinct copy
      // for invalid credentials vs. a not-set-up/disconnected connection.
      return (
        <Paragraph size="extra-small" colorType="secondary">
          {orgEhrUnusableMessage(ehrConnectionStatus)}
        </Paragraph>
      );
    }
    if (startFailureMessage) {
      return (
        <Paragraph size="extra-small" colorType="secondary">
          {startFailureMessage}
        </Paragraph>
      );
    }
    if (noCredentials && !isError) {
      return (
        <Paragraph size="extra-small" colorType="secondary">
          Your EHR is disconnected. Please reconnect to refresh programming data.
        </Paragraph>
      );
    }
    if (credentialsBlocked || importAlertError?.includes("FAILED_CREDS")) {
      return (
        <Paragraph size="extra-small" colorType="secondary">
          Your EHR credentials are invalid. Please re-enter your credentials and try again.
        </Paragraph>
      );
    }
    if (importAlertWarnings?.length) {
      return (
        <>
          {importAlertWarnings.map((warning) => (
            <Paragraph key={warning?.description} size="extra-small" colorType="secondary">
              {warning?.description}
              {!!warning?.affectedItems?.length && (
                <ul className="mt-1">
                  {warning.affectedItems.map((item) => (
                    <li key={item?.message} className="list-disc ml-5">
                      {item?.message}
                    </li>
                  ))}
                </ul>
              )}
            </Paragraph>
          ))}
        </>
      );
    }
    if (importAlertUserMessage) {
      return (
        <Paragraph size="extra-small" colorType="secondary">
          {importAlertUserMessage}
        </Paragraph>
      );
    }
    return (
      <Paragraph size="extra-small" colorType="secondary">
        We weren&apos;t able to refresh <span className="ph-no-capture">{clientName}</span> program data from the EHR.
        No changes were made. If the problem persists, please contact Frontera{" "}
        <Button
          appearance="text"
          text="support"
          onClick={() => window.open(SUPPORT_FORM_URL, "_blank", "noopener,noreferrer")}
          className="font-normal! px-0! text-xs! py-0!"
        />
        .
      </Paragraph>
    );
  };

  const needsReconnect = credentialsBlocked || noCredentials || importAlertError?.includes("FAILED_CREDS");
  let handleRetryRefresh: () => void;
  if (needsReconnect) {
    handleRetryRefresh = () => {
      setMfaImportStarted(false);
      setEhrLoginDialogOpen(true);
    };
  } else if (shouldReconnectFirst) {
    handleRetryRefresh = () => void reconnectThenRefresh();
  } else {
    handleRetryRefresh = clientImportFromEHR;
  }

  return (
    <div className="flex flex-col justify-center items-center pt-16 min-w-full">
      <div className="flex flex-col gap-4 p-8 bg-white rounded-3xl max-w-xl">
        {learningTreeStructureOnly ? (
          <>
            <Headline size="small">
              Reading <span className="ph-no-capture">{clientName}</span> learning tree
            </Headline>
            <Paragraph size="small">
              We read the learning tree from CentralReach to pick up new, removed and moved items. No goal or target
              data is fetched.
            </Paragraph>
          </>
        ) : (
          <>
            <Headline size="small">
              Refreshing <span className="ph-no-capture">{clientName}</span> programming data
            </Headline>
            <Paragraph size="small">
              Before you begin your reassessment, we refresh your client&apos;s latest programming and profile data from
              your EHR. Some updates may take time. You don&apos;t need to stay here — we&apos;ll let you know when
              everything is ready.
            </Paragraph>
          </>
        )}
        {!showError && isLoading && (
          <>
            <div className="flex flex-row gap-2 py-4">
              <Loader className="w-4 h-4" />
              <Paragraph size="small">
                {learningTreeStructureOnly ? "Reading" : "Refreshing"}{" "}
                <span className="ph-no-capture">{clientName}</span>{" "}
                {learningTreeStructureOnly ? "learning tree from CentralReach..." : "data from EHR..."}
              </Paragraph>
            </div>
            <div className="rounded-lg p-2 bg-limestone-50">
              <Paragraph size="extra-small" colorType="secondary">
                This may take a few minutes, you may feel free to do other things — we&apos;ll let you know when
                it&apos;s ready!
              </Paragraph>
            </div>
          </>
        )}
        {showError && (
          <>
            <div className="flex flex-row gap-2 py-4">
              <ExclamationCircleIcon className="w-4 h-4 text-red-700" />
              <Paragraph size="small">
                {learningTreeStructureOnly
                  ? "Failed to read the client's learning tree"
                  : "Failed to refresh client's programming data"}
              </Paragraph>
            </div>
            <div className="rounded-lg p-2 bg-limestone-50 flex flex-col gap-2 items-start">
              <Paragraph size="extra-small" displayType="loud" colorType="secondary">
                We couldn&apos;t update client data
              </Paragraph>
              {renderErrorDetail()}
              {!orgEhrUnusable && (
                <div className="flex flex-row gap-2 items-center py-2">
                  <Button
                    className="min-w-full! text-xs! font-normal! px-0! !-py-0"
                    text="Retry Refresh"
                    onClick={handleRetryRefresh}
                    appearance="text"
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {ehrLoginDialogOpen && (
        <EHRLoginDialog
          open={ehrLoginDialogOpen}
          setOpen={setEhrLoginDialogOpen}
          onMfaCodeSubmitted={() => {
            // The dialog closes once the code is sent, usually before the
            // login reports success. Start the import now; FDM queues it on the
            // login still in progress, and fails it if that login fails.
            // Refreshing the provider's cached "disconnected, no credentials"
            // lets the header badge track the login (incl. a wrong-code
            // re-prompt) and keeps a later Retry from reading stale state.
            void queryClient.invalidateQueries({ queryKey: ["getHasEhrCredentials"] });
            void queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
            setMfaImportStarted(true);
            clientImportFromEHR();
          }}
          setSuccessLogin={(success) => {
            setEhrLoginDialogOpen(false);
            if (success && !mfaImportStarted) clientImportFromEHR();
          }}
        />
      )}
    </div>
  );
};
